import { Users, CheckCircle, AlertCircle } from "lucide-react";

interface RecipientsPreviewProps {
  count?: number;
  recipients?: Array<{
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  }>;
  isLoading?: boolean;
  onCalculate?: () => void;
}

export function RecipientsPreview({
  count = 0,
  recipients = [],
  isLoading = false,
  onCalculate,
}: RecipientsPreviewProps) {
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-[#f09040] to-[#e07828] rounded-lg flex items-center justify-center">
            <Users size={24} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-[#102040]">
              Target Recipients
            </h3>
            <p className="text-sm text-[#989797]">
              {isLoading ? "Calculating..." : `${count} recipient(s)`}
            </p>
          </div>
        </div>
        {onCalculate && (
          <button
            type="button"
            onClick={onCalculate}
            disabled={isLoading}
            className="px-4 py-2 bg-[#e07828] hover:bg-[#c99800] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Calculating..." : "Calculate Recipients"}
          </button>
        )}
      </div>

      {recipients.length > 0 && (
        <div className="mt-6">
          <p className="text-xs font-medium text-[#989797] mb-3">
            Preview (first 10 recipients):
          </p>
          <div className="space-y-2 max-h-[300px] overflow-y-auto custom-scrollbar">
            {recipients.slice(0, 10).map((recipient) => (
              <div
                key={recipient._id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle size={18} className="text-green-500" />
                  <div>
                    <p className="text-sm font-medium text-[#102040]">
                      {recipient.firstName} {recipient.lastName}
                    </p>
                    <p className="text-xs text-[#989797]">{recipient.email}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {count === 0 && !isLoading && (
        <div className="mt-6 flex flex-col items-center justify-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
          <AlertCircle size={40} className="text-[#989797] mb-3" />
          <p className="text-sm font-medium text-[#989797]">
            No recipients match your targeting criteria
          </p>
          <p className="text-xs text-[#989797] mt-1">
            Try adjusting your targeting filters
          </p>
        </div>
      )}
    </div>
  );
}
